import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useDealSources } from "@/lib/crm-source";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import QuickCreateContactDialog from "@/components/Crm/QuickCreateContactDialog";
import ContactSearchCombobox from "@/components/Crm/ContactSearchCombobox";
import {
  CONTACT_TYPE_CFG,
  contactInitials,
  type ContactLite,
} from "@/components/Crm/contact-display";
import {
  Package,
  Plus,
  Trash2,
  Minus,
  Percent,
  Clock,
  Flame,
  AlertCircle,
  MapPin,
  Briefcase,
  Pencil,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Check,
  ChevronsUpDown,
  Loader2,
  Landmark,
  Building2,
  Calendar,
  Banknote,
  CircleDot,
  Mail,
  Target,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { normalizePriceOrZero } from "@/lib/price";

type StageOption = { id: number; name: string; isWon: boolean; isLost: boolean; color: string };

// `initials`, `TYPE_CFG` e o tipo do contato vivem em contact-display.tsx —
// o picker de participantes (CrmDealDetail) usa os mesmos.
const initials = contactInitials;
const TYPE_CFG = CONTACT_TYPE_CFG;
type Contact = ContactLite;

// ─── Preview card do contato selecionado ────────────────────────────────────
function ContactPreviewCard({
  contactId,
  onChange,
}: {
  contactId: number;
  onChange: (c: Contact | null) => void;
}) {
  const [editOpen, setEditOpen] = useState(false);
  const detailQuery = trpc.contacts.get.useQuery({ id: contactId });

  if (detailQuery.isLoading) {
    return (
      <div className="rounded-md border bg-card p-3">
        <div className="h-14 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  const c = detailQuery.data as any;
  if (!c) {
    return (
      <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
        Contato não encontrado.{" "}
        <button
          className="underline"
          onClick={() => onChange(null)}
        >
          Selecionar outro
        </button>
      </div>
    );
  }

  const type = c.contactType ?? "empresa";
  const cfg = TYPE_CFG[type] ?? TYPE_CFG.empresa;
  const TypeIcon = cfg.icon;
  const full = [c.name, c.lastName].filter(Boolean).join(" ");
  const location = [c.city, c.state].filter(Boolean).join(" · ");

  return (
    <>
      <div className="rounded-lg border bg-card overflow-hidden">
        {/* Faixa superior tingida pelo tipo */}
        <div className={`h-1 ${cfg.color.replace("text-", "bg-").split(" ")[0]}`} />
        <div className="p-3 space-y-3">
          <div className="flex items-start gap-3">
            <Avatar className="h-10 w-10 shrink-0">
              <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                {initials(full)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-sm font-semibold truncate">{full}</span>
                <span
                  className={`inline-flex items-center gap-1 rounded ring-1 ${cfg.color} px-1.5 py-0.5 text-[10px] font-semibold`}
                >
                  <TypeIcon className="h-2.5 w-2.5" />
                  {cfg.label.split(" · ")[0]}
                </span>
              </div>
              {c.companyName && (
                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
                  {type === "public_admin" ? (
                    <Landmark className="h-3 w-3 shrink-0" />
                  ) : (
                    <Building2 className="h-3 w-3 shrink-0" />
                  )}
                  {c.companyName}
                </div>
              )}
            </div>
          </div>

          {/* Metadados */}
          <div className="grid grid-cols-2 gap-2 text-xs pt-1 border-t">
            {c.phoneNumber && (
              <div className="flex items-start gap-1.5 min-w-0">
                <span className="text-muted-foreground shrink-0 mt-0.5">📞</span>
                <span className="truncate font-mono tabular-nums">
                  {c.phoneNumber}
                </span>
              </div>
            )}
            {c.email && (
              <div className="flex items-start gap-1.5 min-w-0">
                <Mail className="h-3 w-3 text-muted-foreground shrink-0 mt-0.5" />
                <span className="truncate">{c.email}</span>
              </div>
            )}
            {location && (
              <div className="flex items-start gap-1.5 min-w-0">
                <MapPin className="h-3 w-3 text-muted-foreground shrink-0 mt-0.5" />
                <span className="truncate">{location}</span>
              </div>
            )}
            {type === "public_admin" && (
              <div className="flex items-start gap-1.5 min-w-0">
                <Briefcase className="h-3 w-3 text-muted-foreground shrink-0 mt-0.5" />
                <span
                  className={`truncate ${
                    c.sector ? "" : "text-muted-foreground italic"
                  }`}
                >
                  {c.sector || "Sem setor definido"}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5 pt-1">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onChange(null)}
              className="h-7 flex-1 text-xs"
            >
              Trocar
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditOpen(true)}
              className="h-7 flex-1 text-xs"
            >
              <Pencil className="h-3 w-3 mr-1" />
              Editar
            </Button>
          </div>
        </div>
      </div>

      <QuickCreateContactDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        contactId={contactId}
        onCreated={(updated) => onChange(updated as any)}
      />
    </>
  );
}

// ─── Contact field (wrapper: combobox + preview) ────────────────────────────
function ContactCombobox({
  value,
  onChange,
}: {
  value: Contact | null;
  onChange: (c: Contact | null) => void;
}) {
  if (value) {
    return <ContactPreviewCard contactId={value.id} onChange={onChange} />;
  }
  // defaultOpen: o sheet de nova oportunidade começa no passo "escolher
  // contato", então abrir o picker já aberto poupa um clique.
  return <ContactSearchCombobox onChange={onChange} defaultOpen />;
}

/**
 * Identidade de uma linha de produto na oportunidade.
 *
 * Deixou de ser só o productId porque o mesmo produto pode entrar duas vezes
 * com ofertas diferentes (Presencial e Online). O banco sempre permitiu — não
 * há unique em (dealId, productId) — era a UI que bloqueava.
 */
function selectedProductKey(productId: number, offerId: number | null) {
  return `${productId}:${offerId ?? 0}`;
}

// ─── Product picker (combobox) ───────────────────────────────────────────────
function ProductCombobox({
  excludeKeys,
  onPick,
}: {
  /** Chaves produto:oferta já adicionadas — ver selectedProductKey. */
  excludeKeys: string[];
  onPick: (product: any, offer: any | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const search = trpc.crm.products.list.useQuery(
    { search: query || undefined, limit: 20, withOffers: true },
    { enabled: open },
  );

  // Produto com ofertas vira UMA entrada por oferta: escolher "Curso Online" é
  // um clique só, e Presencial + Online podem coexistir na mesma oportunidade.
  const options = (search.data?.rows ?? []).flatMap((r: any) =>
    (r.offers?.length ?? 0) > 0
      ? r.offers.map((o: any) => ({ product: r.product, offer: o }))
      : [{ product: r.product, offer: null }],
  );
  const rows = options.filter(
    (o: any) => !excludeKeys.includes(selectedProductKey(o.product.id, o.offer?.id ?? null)),
  );

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center justify-between gap-2 rounded-md border border-dashed bg-background px-3 py-2 text-sm hover:bg-muted/40 transition"
        >
          <span className="flex items-center gap-2 text-muted-foreground">
            <Plus className="h-4 w-4" />
            Adicionar produto…
          </span>
          <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por nome ou SKU..."
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {search.isFetching ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 inline animate-spin mr-2" />
                Buscando…
              </div>
            ) : (
              <>
                <CommandEmpty>Nenhum produto encontrado.</CommandEmpty>
                <CommandGroup>
                  {rows.map((row: any) => (
                    <CommandItem
                      key={selectedProductKey(row.product.id, row.offer?.id ?? null)}
                      onSelect={() => {
                        onPick(row.product, row.offer);
                        setOpen(false);
                        setQuery("");
                      }}
                      className="flex items-center gap-2"
                    >
                      <div className="h-8 w-8 rounded-md bg-muted overflow-hidden shrink-0 border">
                        {row.product.imageUrl ? (
                          <img
                            src={row.product.imageUrl}
                            alt=""
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center">
                            <Package className="h-3.5 w-3.5 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {row.product.name}
                          {row.offer && (
                            <span className="text-muted-foreground font-normal">
                              {" · "}
                              {row.offer.name}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2">
                          {row.product.sku && (
                            <span className="font-mono">{row.product.sku}</span>
                          )}
                          <span className="tabular-nums">
                            {Number(row.offer?.price ?? row.product.price ?? 0).toLocaleString(
                              "pt-BR",
                              { style: "currency", currency: "BRL" },
                            )}
                          </span>
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function NewDealSheet({
  open,
  onOpenChange,
  pipelineId,
  stages,
  initialStageId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  pipelineId: number;
  stages: StageOption[];
  /** Pré-seleciona um estágio (usado quando criado de dentro de uma coluna do Kanban). */
  initialStageId?: number | null;
  onCreated?: () => void;
}) {
  type SelectedProduct = {
    /** produto:oferta — ver selectedProductKey. */
    key: string;
    productId: number;
    offerId: number | null;
    offerName: string | null;
    name: string;
    sku: string | null;
    imageUrl: string | null;
    quantity: number;
    unitPrice: string;
    discountPct: number;
  };

  const [, navigate] = useLocation();

  /** Qual dos dois botões disparou a criação — ver o onSuccess da mutation. */
  const openAfterCreateRef = useRef(true);

  const [contact, setContact] = useState<Contact | null>(null);
  const [title, setTitle] = useState("");
  const [value, setValue] = useState("");
  const [stageId, setStageId] = useState<number | null>(null);
  const sources = useDealSources();
  const [sourceId, setSourceId] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [useProductsTotal, setUseProductsTotal] = useState(true);

  // Próxima ação: define se o usuário quer marcar a data prevista de fechamento
  // ou já criar a primeira tarefa vinculada ao lead.
  type NextAction = "none" | "expected" | "task";
  const [nextAction, setNextAction] = useState<NextAction>("none");
  const [expected, setExpected] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDueAt, setTaskDueAt] = useState("");
  const [taskPriority, setTaskPriority] = useState<"low" | "medium" | "high">(
    "medium",
  );

  const firstStage = stages.find((s) => !s.isWon && !s.isLost) ?? stages[0];
  const effectiveStageId = stageId ?? initialStageId ?? firstStage?.id ?? null;

  // Quando o sheet abre com um initialStageId (ex.: clique "+ Adicionar" numa
  // coluna específica do Kanban), pré-seleciona esse estágio.
  useEffect(() => {
    if (open && initialStageId) {
      setStageId(initialStageId);
    }
  }, [open, initialStageId]);

  const productsTotal = useMemo(
    () =>
      selectedProducts.reduce(
        (acc, p) =>
          acc +
          Number(p.quantity ?? 0) *
            Number(p.unitPrice ?? 0) *
            ((100 - Number(p.discountPct ?? 0)) / 100),
        0,
      ),
    [selectedProducts],
  );

  const utils = trpc.useUtils();
  const addProduct = trpc.crm.dealProducts.add.useMutation();
  const createTaskMut = trpc.crm.tasks.create.useMutation();
  const create = trpc.crm.deals.create.useMutation({
    onSuccess: async (deal: any) => {
      if (!deal?.id) {
        toast.error("Falha ao criar oportunidade");
        return;
      }
      // Vincula produtos (se houver) — sequencial para manter ordem.
      if (selectedProducts.length > 0) {
        for (const p of selectedProducts) {
          try {
            await addProduct.mutateAsync({
              dealId: deal.id,
              productId: p.productId,
              offerId: p.offerId,
              quantity: p.quantity,
              unitPrice: normalizePriceOrZero(p.unitPrice),
              discountPct: p.discountPct,
            });
          } catch (err) {
            console.warn("[NewDealSheet] Falha ao vincular produto:", err);
          }
        }
      }
      // Cria primeira tarefa vinculada (se o usuário escolheu essa próxima ação).
      if (nextAction === "task" && taskTitle.trim()) {
        try {
          await createTaskMut.mutateAsync({
            title: taskTitle.trim(),
            dealId: deal.id,
            contactId: contact?.id ?? null,
            dueAt: taskDueAt ? new Date(taskDueAt) : null,
            priority: taskPriority,
          } as any);
        } catch (err) {
          console.warn("[NewDealSheet] Falha ao criar tarefa:", err);
        }
      }
      toast.success("Oportunidade criada");
      utils.crm.deals.kanban.invalidate();
      utils.crm.deals.list.invalidate();
      utils.crm.reports.pipelineSummary.invalidate();
      onCreated?.();
      onOpenChange(false);
      reset();
      // Só "Criar e abrir" navega. O "Criar" fica na lista, para quem está
      // cadastrando várias em sequência. A escolha é lida do ref porque o
      // onSuccess roda depois de todos os awaits acima.
      if (openAfterCreateRef.current) {
        navigate(`/crm/deals/${deal.id}`);
      }
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao criar oportunidade")),
  });

  function reset() {
    setContact(null);
    setTitle("");
    setValue("");
    setStageId(null);
    setSourceId(null);
    setNotes("");
    setExpected("");
    setSelectedProducts([]);
    setUseProductsTotal(true);
    setNextAction("none");
    setTaskTitle("");
    setTaskDueAt("");
    setTaskPriority("medium");
  }

  function submit({ openAfter }: { openAfter: boolean }) {
    openAfterCreateRef.current = openAfter;
    if (!contact) return toast.error("Selecione um contato");
    if (!title.trim()) return toast.error("Título é obrigatório");
    if (!effectiveStageId) return toast.error("Selecione um estágio");
    if (nextAction === "task" && !taskTitle.trim()) {
      return toast.error("Dê um título para a primeira tarefa");
    }
    const numericValue = value.replace(/[^\d,.]/g, "").replace(/\./g, "").replace(",", ".");
    const finalValue =
      selectedProducts.length > 0 && useProductsTotal
        ? productsTotal.toFixed(2)
        : numericValue || "0";
    const expectedDate =
      nextAction === "expected" && expected ? new Date(expected) : null;
    create.mutate({
      title: title.trim(),
      value: finalValue,
      contactId: contact.id,
      pipelineId,
      stageId: effectiveStageId,
      sourceId,
      notes: notes || null,
      expectedCloseDate: expectedDate,
    });
  }

  function addSelectedProduct(product: any, offer: any | null) {
    const key = selectedProductKey(product.id, offer?.id ?? null);
    if (selectedProducts.some((p) => p.key === key)) {
      toast.info(offer ? "Essa oferta já foi adicionada" : "Produto já adicionado");
      return;
    }
    setSelectedProducts((prev) => [
      ...prev,
      {
        key,
        productId: product.id,
        offerId: offer?.id ?? null,
        offerName: offer?.name ?? null,
        name: product.name,
        sku: product.sku ?? null,
        imageUrl: product.imageUrl ?? null,
        quantity: 1,
        // Preço da oferta escolhida; sem oferta, o sugerido do produto.
        unitPrice: String(offer?.price ?? product.price ?? "0"),
        discountPct: 0,
      },
    ]);
  }

  function updateSelectedProduct(key: string, patch: Partial<SelectedProduct>) {
    setSelectedProducts((prev) =>
      prev.map((p) => (p.key === key ? { ...p, ...patch } : p)),
    );
  }

  function removeSelectedProduct(key: string) {
    setSelectedProducts((prev) => prev.filter((p) => p.key !== key));
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[480px] w-full flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Target className="h-5 w-5" />
            </div>
            <div>
              <SheetTitle>Nova oportunidade</SheetTitle>
              <SheetDescription>Cadastre o negócio para acompanhar no funil</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* ── 1. Contato ──────────────────────────── */}
          <section className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              1. Contato principal
            </Label>
            <ContactCombobox value={contact} onChange={setContact} />
            {contact?.contactType === "public_admin" && (
              <p className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1 mt-1">
                <Landmark className="h-3 w-3" />
                B2G — não esqueça de adicionar o decisor nos participantes depois.
              </p>
            )}
          </section>

          <Separator />

          {/* ── 2. Produtos (o que está sendo negociado) ── */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <Package className="h-3 w-3" />
                2. Produtos
              </Label>
              {selectedProducts.length > 0 && (
                <span className="text-xs text-muted-foreground">
                  {selectedProducts.length} vinculado
                  {selectedProducts.length > 1 ? "s" : ""}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              O que está sendo oferecido. O valor da oportunidade é calculado
              automaticamente.
            </p>

            <ProductCombobox
              excludeKeys={selectedProducts.map((p) => p.key)}
              onPick={addSelectedProduct}
            />

            {selectedProducts.length > 0 && (
              <div className="space-y-2">
                {selectedProducts.map((p) => {
                  const lineTotal =
                    p.quantity *
                    Number(p.unitPrice || 0) *
                    ((100 - p.discountPct) / 100);
                  return (
                    <div
                      key={p.key}
                      className="rounded-lg border bg-card p-2.5 space-y-2"
                    >
                      <div className="flex items-start gap-2">
                        <div className="h-9 w-9 rounded-md bg-muted overflow-hidden shrink-0 border">
                          {p.imageUrl ? (
                            <img
                              src={p.imageUrl}
                              alt=""
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center">
                              <Package className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">
                            {p.name}
                            {p.offerName && (
                              <span className="text-muted-foreground font-normal">
                                {" · "}
                                {p.offerName}
                              </span>
                            )}
                          </div>
                          {p.sku && (
                            <div className="text-[11px] text-muted-foreground font-mono">
                              {p.sku}
                            </div>
                          )}
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                          onClick={() => removeSelectedProduct(p.key)}
                          title="Remover"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-[auto_1fr_auto] gap-2 items-end">
                        <div>
                          <Label className="text-[10px] uppercase text-muted-foreground">
                            Qtd
                          </Label>
                          <div className="flex items-center gap-0.5 mt-0.5">
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-7 w-7 shrink-0"
                              onClick={() =>
                                updateSelectedProduct(p.key, {
                                  quantity: Math.max(1, p.quantity - 1),
                                })
                              }
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <Input
                              type="number"
                              min={1}
                              value={p.quantity}
                              onChange={(e) =>
                                updateSelectedProduct(p.key, {
                                  quantity: Math.max(1, Number(e.target.value) || 1),
                                })
                              }
                              className="h-7 w-14 text-center text-sm"
                            />
                            <Button
                              size="icon"
                              variant="outline"
                              className="h-7 w-7 shrink-0"
                              onClick={() =>
                                updateSelectedProduct(p.key, {
                                  quantity: p.quantity + 1,
                                })
                              }
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase text-muted-foreground">
                            Preço unit.
                          </Label>
                          <div className="relative mt-0.5">
                            <Banknote className="h-3.5 w-3.5 absolute left-2 top-1.5 text-muted-foreground" />
                            <Input
                              type="number"
                              step="0.01"
                              value={p.unitPrice}
                              onChange={(e) =>
                                updateSelectedProduct(p.key, {
                                  unitPrice: e.target.value,
                                })
                              }
                              className="pl-7 h-7 text-sm tabular-nums"
                            />
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] uppercase text-muted-foreground">
                            Total
                          </div>
                          <div className="text-sm font-bold tabular-nums mt-0.5">
                            {lineTotal.toLocaleString("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            })}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-[10px] uppercase text-muted-foreground flex items-center gap-1">
                          <Percent className="h-2.5 w-2.5" />
                          Desconto
                        </Label>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          step={5}
                          value={p.discountPct}
                          onChange={(e) =>
                            updateSelectedProduct(p.key, {
                              discountPct: Number(e.target.value),
                            })
                          }
                          className="flex-1 accent-primary"
                        />
                        <span className="text-xs tabular-nums w-10 text-right text-muted-foreground">
                          {p.discountPct}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <Separator />

          {/* ── 3. O negócio ────────────────────────── */}
          <section className="space-y-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              3. O negócio
            </Label>
            <div>
              <Label htmlFor="deal-title" className="text-xs">Título</Label>
              <Input
                id="deal-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={
                  selectedProducts.length === 1
                    ? `Ex: ${selectedProducts[0].name} — ${contact?.companyName ?? contact?.name ?? "cliente"}`
                    : "Ex: Licitação Câmara SP"
                }
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Estágio inicial</Label>
              <div className="mt-2 grid grid-cols-2 gap-1.5">
                {stages
                  .filter((s) => !s.isWon && !s.isLost)
                  .map((s) => {
                    const active = effectiveStageId === s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setStageId(s.id)}
                        className={`flex items-center gap-2 rounded-md border px-2.5 py-2 text-sm transition ${
                          active
                            ? "border-primary bg-primary/5"
                            : "border-border hover:bg-muted/40"
                        }`}
                      >
                        <span
                          className="h-2 w-2 rounded-full shrink-0"
                          style={{ backgroundColor: s.color }}
                        />
                        <span className="truncate">{s.name}</span>
                        {active && <Check className="h-3.5 w-3.5 text-primary ml-auto shrink-0" />}
                      </button>
                    );
                  })}
              </div>
            </div>
            <div>
              <Label className="text-xs">Origem</Label>
              <Select
                value={sourceId == null ? "none" : String(sourceId)}
                onValueChange={(v) => setSourceId(v === "none" ? null : Number(v))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sem origem" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem origem</SelectItem>
                  {sources.options.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      <span className="flex items-center gap-2">
                        <CircleDot className="h-3.5 w-3.5 text-muted-foreground" />
                        {s.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </section>

          <Separator />

          {/* ── 4. Próxima ação ─────────────────────── */}
          <section className="space-y-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              4. Próxima ação
            </Label>
            <p className="text-xs text-muted-foreground">
              Quando você vai voltar para esse lead?
            </p>
            <div className="grid grid-cols-3 gap-1.5">
              {(
                [
                  { key: "none" as const, label: "Só criar", icon: Check },
                  {
                    key: "expected" as const,
                    label: "Previsão",
                    icon: Calendar,
                  },
                  { key: "task" as const, label: "Criar tarefa", icon: Clock },
                ]
              ).map((opt) => {
                const active = nextAction === opt.key;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setNextAction(opt.key)}
                    className={`flex flex-col items-center gap-1 rounded-md border px-2 py-2.5 text-xs transition ${
                      active
                        ? "border-primary bg-primary/5 font-medium"
                        : "border-border hover:bg-muted/40"
                    }`}
                  >
                    <Icon
                      className={`h-4 w-4 ${
                        active ? "text-primary" : "text-muted-foreground"
                      }`}
                    />
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>

            {nextAction === "expected" && (
              <div>
                <Label htmlFor="deal-expected" className="text-xs">
                  Data prevista de fechamento
                </Label>
                <div className="relative mt-1">
                  <Calendar className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground pointer-events-none" />
                  <Input
                    id="deal-expected"
                    type="date"
                    value={expected}
                    onChange={(e) => setExpected(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>
            )}

            {nextAction === "task" && (
              <div className="space-y-3 rounded-lg border bg-muted/30 p-3">
                <div>
                  <Label htmlFor="task-title" className="text-xs">
                    O que precisa fazer?
                  </Label>
                  <Input
                    id="task-title"
                    value={taskTitle}
                    onChange={(e) => setTaskTitle(e.target.value)}
                    placeholder="Ex: Ligar para decisor, enviar proposta..."
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="task-due" className="text-xs">
                      Quando?
                    </Label>
                    <Input
                      id="task-due"
                      type="datetime-local"
                      value={taskDueAt}
                      onChange={(e) => setTaskDueAt(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Prioridade</Label>
                    <div className="mt-1 grid grid-cols-3 gap-1">
                      {(
                        [
                          {
                            key: "low" as const,
                            label: "Baixa",
                            color: "text-slate-600",
                          },
                          {
                            key: "medium" as const,
                            label: "Média",
                            color: "text-amber-600",
                          },
                          {
                            key: "high" as const,
                            label: "Alta",
                            color: "text-red-600",
                            icon: Flame,
                          },
                        ]
                      ).map((p) => {
                        const active = taskPriority === p.key;
                        return (
                          <button
                            key={p.key}
                            type="button"
                            onClick={() => setTaskPriority(p.key)}
                            className={`rounded-md border px-2 py-1.5 text-xs flex items-center justify-center gap-1 transition ${
                              active
                                ? "border-primary bg-primary/5 font-medium"
                                : "border-border hover:bg-muted/40"
                            } ${p.color}`}
                          >
                            {(p as any).icon && (
                              <Flame className="h-3 w-3" />
                            )}
                            {p.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Será criada vinculada à oportunidade e ao contato.
                </p>
              </div>
            )}
          </section>

          <Separator />

          {/* ── 5. Valor & notas (rodapé) ───────────── */}
          <section className="space-y-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              5. Valor & observações
            </Label>

            {selectedProducts.length > 0 ? (
              <div className="rounded-lg bg-primary/5 border border-primary/30 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    Valor da oportunidade
                  </span>
                  <span className="text-lg font-bold tabular-nums">
                    {(useProductsTotal
                      ? productsTotal
                      : Number(value.replace(",", ".")) || 0
                    ).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </span>
                </div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useProductsTotal}
                    onChange={(e) => setUseProductsTotal(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span className="text-xs text-muted-foreground">
                    Usar o total dos produtos como valor
                    {!useProductsTotal && (
                      <span className="block text-[11px] mt-0.5">
                        Valor manual no campo abaixo.
                      </span>
                    )}
                  </span>
                </label>
                {!useProductsTotal && (
                  <div className="relative">
                    <Banknote className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                    <Input
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      placeholder="0,00"
                      className="pl-8 tabular-nums"
                      inputMode="decimal"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div>
                <Label htmlFor="deal-value" className="text-xs">
                  Valor (R$)
                </Label>
                <div className="relative mt-1">
                  <Banknote className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                  <Input
                    id="deal-value"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="0,00"
                    className="pl-8 tabular-nums"
                    inputMode="decimal"
                  />
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Ou adicione produtos acima e o valor é calculado automaticamente.
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="deal-notes" className="text-xs">
                Notas (opcional)
              </Label>
              <Textarea
                id="deal-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Contexto inicial, próximos passos..."
                className="mt-1"
              />
            </div>
          </section>
        </div>

        <div className="border-t px-6 py-4 flex items-center justify-end gap-2 bg-muted/30">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="outline"
            onClick={() => submit({ openAfter: false })}
            disabled={create.isPending}
          >
            Criar
          </Button>
          <Button onClick={() => submit({ openAfter: true })} disabled={create.isPending}>
            {create.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            Criar e abrir
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
